import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2, ArrowRight, Mail, Phone, Hash, User } from 'lucide-react';
import { toast } from 'sonner';
import { Kicker, SmchsLayout } from '@/components/smchs/Shell';
import { registerUser } from '@/services/user-api';

// --- Year/reg group data ---
const YEAR_OPTIONS = Array.from({ length: 13 }, (_, i) => i + 1);

const REG_GROUPS: Record<number, string[]> = {
  1: ['A', 'B', 'C', 'D', 'E'],
  2: ['A', 'B', 'C', 'D', 'E'],
  3: ['A', 'B', 'C', 'D', 'E', 'F'],
  4: ['A', 'B', 'C', 'D', 'E', 'F'],
  5: ['A', 'B', 'C', 'D', 'E', 'F'],
  6: ['A', 'B', 'C', 'D', 'E', 'F'],
  7: ['A', 'B', 'C', 'D', 'E', 'F'],
  8: ['A', 'B', 'C', 'D', 'E', 'F'],
  9: ['A', 'B', 'C', 'D', 'E', 'F'],
  10: ['A', 'B', 'C', 'D', 'E', 'F'],
  11: ['A', 'B', 'C', 'D', 'E', 'F'],
  12: ['SM12', 'SF12', 'AM12', 'AF12'],
  13: ['SM13', 'SF13', 'AM13', 'AF13'],
};

// --- Shared input style (matches SignIn) ---
const inputCls = (hasError?: boolean) =>
  `w-full h-11 px-3 rounded-lg bg-[var(--bg)] border text-[14px] text-[var(--ink)] placeholder:text-[var(--muted)] outline-none transition-colors focus:bg-[var(--surface)] focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/15 ${
    hasError ? 'border-red-500/70' : 'border-[var(--border)]'
  }`;

const labelCls = 'block text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--muted)] mb-2';

type Role = 'student' | 'teacher';

interface FormState {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  school_id: string;
  year_group: number | '';
  reg_group: string;
  password: string;
  confirm_password: string;
}

const emptyForm: FormState = {
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
  school_id: '',
  year_group: '',
  reg_group: '',
  password: '',
  confirm_password: '',
};

const SignUp: React.FC = () => {
  const [role, setRole] = useState<Role>('student');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (field: keyof FormState, value: string | number) => {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: '' }));
    if (field === 'year_group') setForm((p) => ({ ...p, year_group: value as number, reg_group: '' }));
  };

  const switchRole = (r: Role) => {
    setRole(r);
    setForm(emptyForm);
    setErrors({});
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.first_name.trim()) next.first_name = 'Required';
    if (!form.last_name.trim()) next.last_name = 'Required';
    if (!form.email.trim()) next.email = 'Required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) next.email = 'Enter a valid school email';
    if (!form.school_id.trim()) next.school_id = 'Required';
    if (role === 'student') {
      if (!form.year_group) next.year_group = 'Required';
      if (!form.reg_group) next.reg_group = 'Required';
    }
    if (!form.password) next.password = 'Required';
    else if (form.password.length < 8) next.password = 'Minimum 8 characters';
    if (!form.confirm_password) next.confirm_password = 'Required';
    else if (form.password !== form.confirm_password) next.confirm_password = "Passwords don't match";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    try {
      await registerUser({
        role,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone_number: form.phone_number.trim() || undefined,
        school_id: form.school_id.trim(),
        password: form.password,
        confirm_password: form.confirm_password,
        ...(role === 'student' && {
          year_group: form.year_group as number,
          reg_group: form.reg_group,
        }),
      });
      setSubmitted(true);
    } catch (err: any) {
      const data = err?.response?.data;
      if (data && typeof data === 'object') {
        const fieldErrors: Record<string, string> = {};
        Object.entries(data).forEach(([key, val]) => {
          fieldErrors[key] = Array.isArray(val) ? val[0] : String(val);
        });
        setErrors(fieldErrors);
        toast.error('Please fix the errors below.');
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const regGroupOptions = role === 'student' && form.year_group
    ? REG_GROUPS[form.year_group as number] ?? []
    : [];

  return (
    <SmchsLayout>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_55%_at_50%_-5%,rgba(30,45,92,0.14),transparent_60%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:56px_56px] [mask-image:radial-gradient(ellipse_60%_70%_at_50%_10%,black,transparent_70%)] opacity-40" />
        </div>

        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-14 md:py-20 grid md:grid-cols-12 gap-10 md:gap-14 items-start">
          {/* Left: editorial */}
          <div className="md:col-span-6 lg:col-span-7">
            <Kicker label="Join the library" />
            <h1 className="mt-5 font-display font-[900] text-[2.25rem] sm:text-[3rem] md:text-[3.75rem] leading-[0.98] tracking-[-0.03em] text-[var(--ink)] max-w-[18ch]">
              Get your{' '}
              <span className="font-serif italic font-normal text-[var(--primary)]">library card</span>.
            </h1>
            <p className="mt-6 max-w-[52ch] text-[1rem] md:text-[1.05rem] leading-[1.55] text-[var(--ink-soft)]">
              Register with your SMCHS school email. A librarian will review your request and send you an
              approval email before you can sign in.
            </p>
            <div className="hidden md:grid mt-10 grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)] rounded-2xl overflow-hidden max-w-[520px]">
              {[
                { num: '24K', label: 'Titles' },
                { num: '1.2K', label: 'Members' },
                { num: '6', label: 'Book clubs' },
              ].map((s) => (
                <div key={s.label} className="bg-[var(--surface)] p-5">
                  <div className="font-display text-[1.5rem] font-semibold text-[var(--ink)] tracking-[-0.02em]">{s.num}</div>
                  <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--muted)]">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: form card */}
          <div className="md:col-span-6 lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="relative bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-7 md:p-9 shadow-[0_30px_60px_-30px_rgba(10,18,40,0.18)]"
            >
              <div className="absolute -top-px left-7 right-7 h-px bg-gradient-to-r from-transparent via-[var(--gold)] to-transparent" />

              <div className="flex items-center justify-between">
                <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--muted)]">
                  <span className="text-[var(--gold-text)] font-semibold">02</span>
                  <span className="mx-2.5 inline-block w-5 h-px align-middle bg-[var(--border)]" />
                  Register
                </div>
                <Link to="/" className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--muted)] hover:text-[var(--ink)] transition-colors">
                  ← Home
                </Link>
              </div>

              {submitted ? (
                <div className="mt-8 text-center space-y-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--primary)]/10 flex items-center justify-center mx-auto">
                    <svg className="w-6 h-6 text-[var(--primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="font-display font-bold text-[1.25rem] text-[var(--ink)] tracking-[-0.02em]">Account submitted</h2>
                  <p className="text-[13px] text-[var(--ink-soft)] leading-relaxed max-w-[32ch] mx-auto">
                    Your account is pending approval. A librarian will review it and send the approval to{' '}
                    <span className="text-[var(--ink)] font-medium">{form.email}</span>.
                  </p>
                  <Link
                    to="/signin"
                    className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--primary)] hover:opacity-80 transition-opacity mt-2"
                  >
                    Go to sign in <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              ) : (
                <>
                  <h2 className="mt-5 font-display font-bold text-[1.5rem] text-[var(--ink)] tracking-[-0.02em]">Create account</h2>
                  <p className="mt-1.5 text-[13px] text-[var(--ink-soft)]">Register with your SMCHS credentials.</p>

                  {/* Role switcher */}
                  <div className="mt-5 flex rounded-lg border border-[var(--border)] overflow-hidden text-[12px] font-mono uppercase tracking-[0.15em]">
                    {(['student', 'teacher'] as Role[]).map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => switchRole(r)}
                        className={`flex-1 py-2.5 transition-colors ${
                          role === r
                            ? 'bg-[var(--primary)] text-[var(--paper)]'
                            : 'bg-[var(--bg)] text-[var(--muted)] hover:text-[var(--ink)]'
                        }`}
                      >
                        {r === 'student' ? 'Student' : 'Teacher'}
                      </button>
                    ))}
                  </div>

                  <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                    {/* First + Last name */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>First name</label>
                        <div className="relative">
                          <User className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted)]" />
                          <input
                            type="text"
                            placeholder="Ali"
                            value={form.first_name}
                            onChange={(e) => set('first_name', e.target.value)}
                            disabled={isLoading}
                            className={inputCls(!!errors.first_name) + ' pl-10'}
                          />
                        </div>
                        {errors.first_name && <p className="mt-1 text-[12px] text-red-500">{errors.first_name}</p>}
                      </div>
                      <div>
                        <label className={labelCls}>Last name</label>
                        <input
                          type="text"
                          placeholder="Hassan"
                          value={form.last_name}
                          onChange={(e) => set('last_name', e.target.value)}
                          disabled={isLoading}
                          className={inputCls(!!errors.last_name)}
                        />
                        {errors.last_name && <p className="mt-1 text-[12px] text-red-500">{errors.last_name}</p>}
                      </div>
                    </div>

                    {/* Student-only: Year group + Reg group */}
                    {role === 'student' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>Year group</label>
                          <select
                            value={form.year_group}
                            onChange={(e) => set('year_group', Number(e.target.value))}
                            disabled={isLoading}
                            className={inputCls(!!errors.year_group)}
                          >
                            <option value="">Select year</option>
                            {YEAR_OPTIONS.map((y) => (
                              <option key={y} value={y}>Year {y}</option>
                            ))}
                          </select>
                          {errors.year_group && <p className="mt-1 text-[12px] text-red-500">{errors.year_group}</p>}
                        </div>
                        <div>
                          <label className={labelCls}>Reg group</label>
                          <select
                            value={form.reg_group}
                            onChange={(e) => set('reg_group', e.target.value)}
                            disabled={isLoading || !form.year_group}
                            className={inputCls(!!errors.reg_group)}
                          >
                            <option value="">Select group</option>
                            {regGroupOptions.map((g) => (
                              <option key={g} value={g}>{g}</option>
                            ))}
                          </select>
                          {errors.reg_group && <p className="mt-1 text-[12px] text-red-500">{errors.reg_group}</p>}
                        </div>
                      </div>
                    )}

                    {/* School ID */}
                    <div>
                      <label className={labelCls}>School ID</label>
                      <div className="relative">
                        <Hash className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted)]" />
                        <input
                          type="text"
                          placeholder={role === 'student' ? 'e.g. STU001' : 'e.g. EMP001'}
                          value={form.school_id}
                          onChange={(e) => set('school_id', e.target.value)}
                          disabled={isLoading}
                          className={inputCls(!!errors.school_id) + ' pl-10'}
                        />
                      </div>
                      {errors.school_id && <p className="mt-1 text-[12px] text-red-500">{errors.school_id}</p>}
                    </div>

                    {/* School email */}
                    <div>
                      <label className={labelCls}>School email</label>
                      <div className="relative">
                        <Mail className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted)]" />
                        <input
                          id="email"
                          type="email"
                          autoComplete="email"
                          placeholder="you@smchs.edu.pk"
                          value={form.email}
                          onChange={(e) => set('email', e.target.value)}
                          disabled={isLoading}
                          className={inputCls(!!errors.email) + ' pl-10'}
                        />
                      </div>
                      {errors.email && <p className="mt-1 text-[12px] text-red-500">{errors.email}</p>}
                      <p className="mt-1 text-[11px] text-[var(--muted)]">Used as your username</p>
                    </div>

                    {/* Phone (optional) */}
                    <div>
                      <label className={labelCls}>Phone <span className="normal-case tracking-normal opacity-60">(optional)</span></label>
                      <div className="relative">
                        <Phone className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted)]" />
                        <input
                          type="tel"
                          placeholder="+92 300 0000000"
                          value={form.phone_number}
                          onChange={(e) => set('phone_number', e.target.value)}
                          disabled={isLoading}
                          className={inputCls() + ' pl-10'}
                        />
                      </div>
                    </div>

                    {/* Password + Confirm */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Password</label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Min 8 chars"
                            value={form.password}
                            onChange={(e) => set('password', e.target.value)}
                            disabled={isLoading}
                            className={inputCls(!!errors.password) + ' pr-10'}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((s) => !s)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--ink)]"
                            aria-label={showPassword ? 'Hide' : 'Show'}
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {errors.password && <p className="mt-1 text-[12px] text-red-500">{errors.password}</p>}
                      </div>
                      <div>
                        <label className={labelCls}>Confirm</label>
                        <div className="relative">
                          <input
                            type={showConfirm ? 'text' : 'password'}
                            placeholder="Repeat"
                            value={form.confirm_password}
                            onChange={(e) => set('confirm_password', e.target.value)}
                            disabled={isLoading}
                            className={inputCls(!!errors.confirm_password) + ' pr-10'}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirm((s) => !s)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--ink)]"
                            aria-label={showConfirm ? 'Hide' : 'Show'}
                          >
                            {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {errors.confirm_password && <p className="mt-1 text-[12px] text-red-500">{errors.confirm_password}</p>}
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="group w-full h-11 rounded-lg bg-[var(--ink)] text-[var(--paper)] font-display font-semibold text-[14px] tracking-[-0.005em] flex items-center justify-center gap-2 hover:brightness-110 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_8px_24px_-10px_rgba(10,18,40,0.45)]"
                    >
                      {isLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
                      ) : (
                        <>Submit registration <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" /></>
                      )}
                    </button>

                    <div className="pt-2 border-t border-[var(--border)] text-center">
                      <p className="pt-4 text-[13px] text-[var(--ink-soft)]">
                        Already have an account?{' '}
                        <Link to="/signin" className="font-medium text-[var(--primary)] hover:opacity-80 transition-opacity">
                          Sign in
                        </Link>
                      </p>
                    </div>
                  </form>
                </>
              )}
            </motion.div>

            <p className="mt-5 text-center text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--muted)]">
              Secured · SMCHS Library · libapi.smchs.cloud
            </p>
          </div>
        </div>
      </section>
    </SmchsLayout>
  );
};

export default SignUp;
